import { z } from 'zod';

const location = z.object({
  funnel_id: z.string(),
  funnel_version: z.number(),
  origin: z.string(),
  query: z.string(),
  query_params: z.record(z.string(), z.string()).optional(),
  sandbox: z.boolean(),
});

const profile = z.object({
  id: z.string(),
  session_id: z.string(),
  country: z.string(),
  ip: z.string(),
  city: z.string(),
  time_zone: z.string(),
  user_agent: z.string(),
  locale: z.string(),
});

const screenPresentedSchema = z.object({
  type: z.literal('screen.presented'),
  location,
  profile,
  data: z.any(), // define when needed
});

const onboardingStartedSchema = z.object({
  type: z.literal('onboarding.started'),
  location,
  profile,
  data: z.any(), // define when needed
});

const subscriptionCreatedSchema = z.object({
  type: z.literal('subscription.created'),
  location,
  profile,
  data: z.object({
    session_id: z.string(),
    vendor: z.string(),
    vendor_subscription_id: z.string(),
    vendor_profile_id: z.string(),
    vendor_product_id: z.string(),
  }),
});

const subscriptionTrialingSchema = z.object({
  type: z.literal('subscription.trialing'),
  location,
  profile,
  data: z.any(), // define when needed
});

const subscriptionActiveSchema = z.object({
  type: z.literal('subscription.active'),
  location,
  profile,
  data: z.object({
    session_id: z.string(),
    starts_at: z.string(),
    vendor: z.string(),
    vendor_product_id: z.string(),
    vendor_profile_id: z.string(),
    vendor_subscription_id: z.string(),
    entitlement: z.string().nullable(),
    expires_at: z.string().nullable(),
    vendor_transaction_id: z.string().nullable(),
  }),
});

const subscriptionCycleSchema = z.object({
  type: z.literal('subscription.cycle'),
  location,
  profile,
  data: z.any(), // define when needed
});

const subscriptionPauseSchema = z.object({
  type: z.literal('subscription.pause'),
  location,
  profile,
  data: z.any(), // define when needed
});

const subscriptionResumedSchema = z.object({
  type: z.literal('subscription.resumed'),
  location,
  profile,
  data: z.any(), // define when needed
});

const subscriptionCancelledSchema = z.object({
  type: z.literal('subscription.cancelled'),
  location,
  profile,
  data: z.any(), // define when needed
});

const subscriptionUnpaidSchema = z.object({
  type: z.literal('subscription.unpaid'),
  location,
  profile,
  data: z.any(), // define when needed
});

const onboardingCompletedSchema = z.object({
  type: z.literal('onboarding.completed'),
  location,
  profile,
  data: z.any(), // define when needed
});

const profileUpdatedSchema = z.object({
  type: z.literal('profile.updated'),
  location,
  profile,
  data: z.object({
    id: z.string(),
    email: z.string(),
  }),
});

export type FunnelFoxProfileUpdated = z.infer<typeof profileUpdatedSchema>;

const purchaseCompletedSchema = z.object({
  type: z.literal('purchase.completed'),
  location,
  profile,
  data: z.object({
    vendor: z.string(),
    vendor_profile_id: z.string(),
    subscription: z
      .object({
        vendor_subscription_id: z.string(),
      })
      .nullish(),
  }),
});

export type FunnelFoxPurchaseCompleted = z.infer<typeof purchaseCompletedSchema>;

export type FunnelFoxSubscriptionCreated = z.infer<typeof subscriptionCreatedSchema>;

export type FunnelFoxSubscriptionActive = z.infer<typeof subscriptionActiveSchema>;

// Create the discriminated union schema
export const funnelFoxEventSchema = z.discriminatedUnion('type', [
  onboardingStartedSchema,
  onboardingCompletedSchema,
  profileUpdatedSchema,
  purchaseCompletedSchema,
  screenPresentedSchema,
  subscriptionCreatedSchema,
  subscriptionTrialingSchema,
  subscriptionActiveSchema,
  subscriptionCycleSchema,
  subscriptionPauseSchema,
  subscriptionResumedSchema,
  subscriptionCancelledSchema,
  subscriptionUnpaidSchema,
]);
